// src/utils/mlProcessing.js
// Pure JS ML algorithms — no dependencies, all real implementations
// Covers: Feature Engineering, Regression, Classification, Clustering, Model Evaluation

// ─────────────────────────────────────────────
// DATA LOADING + TYPE INFERENCE (shared)
// ─────────────────────────────────────────────

export const parseNum = (v) => {
  if (v === '' || v === null || v === undefined) return NaN;
  const s = String(v).replace(/[$€£¥,%]/g, '').replace(/,/g, '').trim();
  return parseFloat(s);
};

export const inferTypes = (headers, rows) => {
  return headers.map(col => {
    const vals = rows.map(r => r[col]).filter(v => v !== '' && v !== null && v !== undefined);
    if (!vals.length) return { col, type: 'empty', nullCount: rows.length, uniqueCount: 0 };
    const nullCount = rows.length - vals.length;
    const numVals = vals.filter(v => !isNaN(parseNum(v)));
    const numRatio = numVals.length / vals.length;
    if (numRatio > 0.8) return { col, type: 'numeric', nullCount, uniqueCount: new Set(vals).size };
    return { col, type: 'categorical', nullCount, uniqueCount: new Set(vals).size };
  });
};

export const parseCSVForML = (text) => {
  const lines = text.trim().split('\n').filter(l => l.trim());
  if (lines.length < 2) return null;
  const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
  const rows = lines.slice(1).map(line => {
    const vals = line.match(/(".*?"|[^,]+)/g) || [];
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (vals[i] || '').trim().replace(/^"|"$/g, ''); });
    return obj;
  }).filter(r => Object.values(r).some(v => v !== ''));
  return { headers, rows, shape: [rows.length, headers.length] };
};

// ─────────────────────────────────────────────
// STATISTICS UTILS
// ─────────────────────────────────────────────

export const mean = arr => arr.reduce((s, v) => s + v, 0) / arr.length;
export const std = arr => {
  const m = mean(arr);
  return Math.sqrt(arr.reduce((s, v) => s + (v - m) ** 2, 0) / arr.length);
};
export const variance = arr => { const m = mean(arr); return arr.reduce((s, v) => s + (v - m) ** 2, 0) / arr.length; };
export const covariance = (a, b) => {
  const ma = mean(a), mb = mean(b);
  return a.reduce((s, v, i) => s + (v - ma) * (b[i] - mb), 0) / a.length;
};
export const pearson = (a, b) => {
  const sa = std(a), sb = std(b);
  return sa && sb ? covariance(a, b) / (sa * sb) : 0;
};
export const minMaxScale = (arr) => {
  const mn = Math.min(...arr), mx = Math.max(...arr), r = mx - mn;
  return r === 0 ? arr.map(() => 0) : arr.map(v => (v - mn) / r);
};
export const zScore = (arr) => {
  const m = mean(arr), s = std(arr);
  return s === 0 ? arr.map(() => 0) : arr.map(v => (v - m) / s);
};
export const euclidean = (a, b) => Math.sqrt(a.reduce((s, v, i) => s + (v - b[i]) ** 2, 0));
export const manhattan = (a, b) => a.reduce((s, v, i) => s + Math.abs(v - b[i]), 0);

// ─────────────────────────────────────────────
// FEATURE ENGINEERING
// ─────────────────────────────────────────────

export const featureStats = (rows, col) => {
  const vals = rows.map(r => parseNum(r[col])).filter(v => !isNaN(v) && isFinite(v)).sort((a, b) => a - b);
  if (!vals.length) return null;
  const n = vals.length;
  const m = mean(vals), s = std(vals), v = variance(vals);
  const q1 = vals[Math.floor(n * 0.25)], q3 = vals[Math.floor(n * 0.75)];
  const iqr = q3 - q1;
  const skew = s > 0 ? (vals.reduce((sum, x) => sum + (x - m) ** 3, 0) / n) / s ** 3 : 0;
  const kurt = s > 0 ? (vals.reduce((sum, x) => sum + (x - m) ** 4, 0) / n) / s ** 4 - 3 : 0;
  const outliers = vals.filter(v => v < q1 - 1.5 * iqr || v > q3 + 1.5 * iqr);
  return {
    n, mean: +m.toFixed(4), std: +s.toFixed(4), variance: +v.toFixed(4),
    min: vals[0], max: vals[n - 1], range: +(vals[n - 1] - vals[0]).toFixed(4),
    q1: +q1.toFixed(4), median: +vals[Math.floor(n / 2)].toFixed(4), q3: +q3.toFixed(4), iqr: +iqr.toFixed(4),
    skewness: +skew.toFixed(4), kurtosis: +kurt.toFixed(4),
    outlierCount: outliers.length, outlierPct: +(outliers.length / n * 100).toFixed(1),
    cv: m !== 0 ? +(s / Math.abs(m) * 100).toFixed(2) : 0,
    zeros: vals.filter(v => v === 0).length,
  };
};

// One-hot encode a categorical column
export const oneHotEncode = (rows, col) => {
  const vals = [...new Set(rows.map(r => r[col]).filter(Boolean))].sort();
  const newCols = vals.map(v => `${col}_${v}`);
  const encoded = rows.map(r => {
    const obj = { ...r };
    vals.forEach(v => { obj[`${col}_${v}`] = r[col] === v ? '1' : '0'; });
    return obj;
  });
  return { rows: encoded, newCols, categories: vals };
};

// Label encode
export const labelEncode = (rows, col) => {
  const vals = [...new Set(rows.map(r => r[col]).filter(Boolean))].sort();
  const map = Object.fromEntries(vals.map((v, i) => [v, i]));
  return {
    rows: rows.map(r => ({ ...r, [`${col}_encoded`]: String(map[r[col]] ?? '') })),
    mapping: map,
    newCol: `${col}_encoded`,
  };
};

// Bin a numeric column
export const binColumn = (rows, col, nBins = 5, strategy = 'equal_width') => {
  const vals = rows.map(r => parseNum(r[col])).filter(v => !isNaN(v) && isFinite(v));
  let edges;
  if (strategy === 'equal_width') {
    const mn = Math.min(...vals), mx = Math.max(...vals), w = (mx - mn) / nBins;
    edges = Array.from({ length: nBins + 1 }, (_, i) => mn + i * w);
  } else {
    // equal_freq
    const sorted = [...vals].sort((a, b) => a - b);
    edges = Array.from({ length: nBins + 1 }, (_, i) => sorted[Math.floor(i * sorted.length / nBins)] ?? sorted[sorted.length - 1]);
  }
  const labels = edges.slice(0, -1).map((e, i) => `[${e.toFixed(2)}, ${edges[i + 1].toFixed(2)})`);
  return {
    rows: rows.map(r => {
      const v = parseNum(r[col]);
      if (isNaN(v)) return { ...r, [`${col}_bin`]: '' };
      const bi = Math.min(nBins - 1, edges.slice(1).findIndex(e => v <= e));
      return { ...r, [`${col}_bin`]: labels[bi >= 0 ? bi : nBins - 1] };
    }),
    edges, labels, newCol: `${col}_bin`,
  };
};

// Polynomial features for two columns
export const polyFeatures = (rows, c1, c2, degree = 2) => {
  const newCols = [];
  const result = rows.map(r => {
    const obj = { ...r };
    const v1 = parseNum(r[c1]), v2 = parseNum(r[c2]);
    for (let d = 2; d <= degree; d++) {
      for (let p = 0; p <= d; p++) {
        const colName = `${c1}^${d - p}_${c2}^${p}`;
        obj[colName] = String(+(Math.pow(v1, d - p) * Math.pow(v2, p)).toFixed(4));
        if (!newCols.includes(colName)) newCols.push(colName);
      }
    }
    return obj;
  });
  return { rows: result, newCols };
};

// Interaction features
export const interactionFeature = (rows, c1, c2) => ({
  rows: rows.map(r => ({ ...r, [`${c1}_x_${c2}`]: String(+(parseNum(r[c1]) * parseNum(r[c2])).toFixed(4)) })),
  newCol: `${c1}_x_${c2}`,
});

// Log transform
export const logTransform = (rows, col) => ({
  rows: rows.map(r => {
    const v = parseNum(r[col]);
    return { ...r, [`${col}_log`]: !isNaN(v) && v > 0 ? String(+Math.log(v).toFixed(4)) : '' };
  }),
  newCol: `${col}_log`,
});

// Normalize whole dataset (min-max or z-score)
export const normalizeDataset = (rows, numCols, method = 'minmax') => {
  const scalers = {};
  numCols.forEach(col => {
    const vals = rows.map(r => parseNum(r[col])).filter(v => !isNaN(v) && isFinite(v));
    scalers[col] = method === 'minmax'
      ? { min: Math.min(...vals), max: Math.max(...vals), mean: 0, std: 1 }
      : { mean: mean(vals), std: std(vals), min: 0, max: 1 };
  });
  const normalized = rows.map(r => {
    const obj = { ...r };
    numCols.forEach(col => {
      const v = parseNum(r[col]);
      if (isNaN(v)) return;
      const s = scalers[col];
      obj[col] = method === 'minmax'
        ? String(+((v - s.min) / Math.max(s.max - s.min, 1e-10)).toFixed(4))
        : String(s.std > 0 ? +((v - s.mean) / s.std).toFixed(4) : 0);
    });
    return obj;
  });
  return { rows: normalized, scalers };
};

// Mutual information (discrete approximation via entropy)
export const mutualInfo = (rows, col1, col2) => {
  const bins = 10;
  const v1 = rows.map(r => parseNum(r[col1])).filter(v => !isNaN(v));
  const v2 = rows.map(r => parseNum(r[col2])).filter(v => !isNaN(v));
  if (!v1.length || !v2.length) return 0;
  const min1 = Math.min(...v1), max1 = Math.max(...v1), r1 = max1 - min1 || 1;
  const min2 = Math.min(...v2), max2 = Math.max(...v2), r2 = max2 - min2 || 1;
  const n = Math.min(v1.length, v2.length);
  const joint = {};
  const p1 = {}, p2 = {};
  for (let i = 0; i < n; i++) {
    const b1 = Math.min(bins - 1, Math.floor((v1[i] - min1) / r1 * bins));
    const b2 = Math.min(bins - 1, Math.floor((v2[i] - min2) / r2 * bins));
    const k = `${b1}_${b2}`;
    joint[k] = (joint[k] || 0) + 1;
    p1[b1] = (p1[b1] || 0) + 1;
    p2[b2] = (p2[b2] || 0) + 1;
  }
  let mi = 0;
  for (const k in joint) {
    const [b1, b2] = k.split('_');
    const pxy = joint[k] / n, px = p1[b1] / n, py = p2[b2] / n;
    if (pxy > 0 && px > 0 && py > 0) mi += pxy * Math.log2(pxy / (px * py));
  }
  return +Math.max(0, mi).toFixed(4);
};

// PCA (2D projection via covariance matrix + power iteration)
export const runPCA = (rows, numCols, nComponents = 2) => {
  const cols = numCols.slice(0, 8);
  const mat = rows.map(r => cols.map(c => parseNum(r[c]))).filter(row => row.every(v => !isNaN(v) && isFinite(v)));
  if (mat.length < 3) return null;
  const n = mat.length, d = cols.length;

  // Center
  const means = cols.map((_, ci) => mat.reduce((s, r) => s + r[ci], 0) / n);
  const centered = mat.map(r => r.map((v, ci) => v - means[ci]));

  // Covariance matrix
  const cov = Array.from({ length: d }, (_, i) =>
    Array.from({ length: d }, (_, j) =>
      centered.reduce((s, r) => s + r[i] * r[j], 0) / (n - 1)
    )
  );

  // Power iteration for top-k eigenvectors
  const normalize = v => { const mag = Math.sqrt(v.reduce((s, x) => s + x * x, 0)); return mag ? v.map(x => x / mag) : v; };
  const matVec = (M, v) => M.map(row => row.reduce((s, x, j) => s + x * v[j], 0));
  const dot = (a, b) => a.reduce((s, x, i) => s + x * b[i], 0);

  const eigenvecs = [];
  const eigenvals = [];
  let deflated = cov.map(r => [...r]);

  for (let k = 0; k < Math.min(nComponents, d); k++) {
    let v = Array.from({ length: d }, () => Math.random() - 0.5);
    v = normalize(v);
    for (let iter = 0; iter < 100; iter++) {
      v = normalize(matVec(deflated, v));
    }
    const eigenval = dot(v, matVec(deflated, v));
    eigenvecs.push(v);
    eigenvals.push(eigenval);
    // Deflate
    deflated = deflated.map((row, i) => row.map((val, j) => val - eigenval * v[i] * v[j]));
  }

  const totalVar = eigenvals.reduce((s, v) => s + Math.abs(v), 0) || 1;
  const explainedVar = eigenvals.map(v => +(Math.abs(v) / totalVar * 100).toFixed(2));

  // Project
  const projected = centered.map(row => ({
    pc1: +eigenvecs[0].reduce((s, v, i) => s + v * row[i], 0).toFixed(4),
    pc2: eigenvecs[1] ? +eigenvecs[1].reduce((s, v, i) => s + v * row[i], 0).toFixed(4) : 0,
  }));

  // Loadings
  const loadings = cols.map((col, ci) => ({
    col,
    pc1: +eigenvecs[0][ci].toFixed(4),
    pc2: eigenvecs[1] ? +eigenvecs[1][ci].toFixed(4) : 0,
  }));

  return { projected, loadings, explainedVar, eigenvals: eigenvals.map(v => +v.toFixed(4)), cols };
};

// ─────────────────────────────────────────────
// REGRESSION
// ─────────────────────────────────────────────

// Simple linear regression
export const linearRegression = (rows, xCol, yCol) => {
  const pairs = rows
    .map(r => [parseNum(r[xCol]), parseNum(r[yCol])])
    .filter(([x, y]) => !isNaN(x) && !isNaN(y) && isFinite(x) && isFinite(y));
  const n = pairs.length;
  if (n < 2) return null;
  const mx = mean(pairs.map(([x]) => x));
  const my = mean(pairs.map(([, y]) => y));
  let num = 0, den = 0;
  pairs.forEach(([x, y]) => { num += (x - mx) * (y - my); den += (x - mx) ** 2; });
  const slope = den ? num / den : 0;
  const intercept = my - slope * mx;
  const predictions = pairs.map(([x]) => slope * x + intercept);
  const residuals = pairs.map(([, y], i) => y - predictions[i]);
  const ssRes = residuals.reduce((s, v) => s + v ** 2, 0);
  const ssTot = pairs.map(([, y]) => (y - my) ** 2).reduce((s, v) => s + v, 0);
  const r2 = ssTot ? 1 - ssRes / ssTot : 0;
  const mse = ssRes / n;
  const mae = residuals.reduce((s, v) => s + Math.abs(v), 0) / n;
  return {
    slope: +slope.toFixed(6), intercept: +intercept.toFixed(6),
    r2: +r2.toFixed(4), adjR2: +(1 - (1 - r2) * (n - 1) / (n - 2)).toFixed(4),
    mse: +mse.toFixed(4), rmse: +Math.sqrt(mse).toFixed(4), mae: +mae.toFixed(4),
    n, pairs, predictions, residuals,
    equation: `y = ${slope.toFixed(4)}x + (${intercept.toFixed(4)})`,
  };
};

// Multiple linear regression (gradient descent)
export const multiLinearRegression = (rows, xCols, yCol, opts = {}) => {
  const { lr = 0.01, epochs = 1000, regularize = 0 } = opts;
  const data = rows.map(r => ({
    x: xCols.map(c => parseNum(r[c])),
    y: parseNum(r[yCol]),
  })).filter(d => d.x.every(v => !isNaN(v) && isFinite(v)) && !isNaN(d.y) && isFinite(d.y));
  if (data.length < xCols.length + 1) return null;
  const n = data.length;

  // Normalize inputs
  const scalers = xCols.map((_, ci) => {
    const v = data.map(d => d.x[ci]);
    return { m: mean(v), s: std(v) || 1 };
  });
  const Xnorm = data.map(d => d.x.map((v, ci) => (v - scalers[ci].m) / scalers[ci].s));
  const Y = data.map(d => d.y);
  const ym = mean(Y), ys = std(Y) || 1;
  const Ynorm = Y.map(v => (v - ym) / ys);

  let weights = new Array(xCols.length).fill(0);
  let bias = 0;
  const losses = [];

  for (let epoch = 0; epoch < epochs; epoch++) {
    let wGrad = new Array(xCols.length).fill(0);
    let bGrad = 0;
    let loss = 0;
    Xnorm.forEach((x, i) => {
      const pred = x.reduce((s, v, j) => s + v * weights[j], bias);
      const err = pred - Ynorm[i];
      loss += err ** 2;
      x.forEach((v, j) => { wGrad[j] += err * v; });
      bGrad += err;
    });
    loss /= n;
    if (epoch % 50 === 0) losses.push({ epoch, loss: +loss.toFixed(6) });
    weights = weights.map((w, j) => w - lr * (wGrad[j] / n + regularize * w));
    bias -= lr * bGrad / n;
  }

  // Predictions (in original scale)
  const predictions = data.map((d, i) => {
    const predNorm = Xnorm[i].reduce((s, v, j) => s + v * weights[j], bias);
    return predNorm * ys + ym;
  });
  const residuals = data.map((d, i) => d.y - predictions[i]);
  const ssRes = residuals.reduce((s, v) => s + v ** 2, 0);
  const ssTot = Y.map(y => (y - ym) ** 2).reduce((s, v) => s + v, 0);
  const r2 = ssTot ? 1 - ssRes / ssTot : 0;
  const mse = ssRes / n;

  // Denorm weights for reporting
  const realWeights = weights.map((w, j) => +(w * ys / scalers[j].s).toFixed(4));

  return {
    weights: realWeights, bias: +(bias * ys + ym - realWeights.reduce((s, w, j) => s + w * scalers[j].m, 0)).toFixed(4),
    r2: +r2.toFixed(4), adjR2: +(1 - (1 - r2) * (n - 1) / (n - xCols.length - 1)).toFixed(4),
    mse: +mse.toFixed(4), rmse: +Math.sqrt(mse).toFixed(4),
    mae: +(residuals.reduce((s, v) => s + Math.abs(v), 0) / n).toFixed(4),
    n, lossHistory: losses, predictions, residuals, xCols,
    featureImportance: xCols.map((col, j) => ({ col, weight: realWeights[j], absWeight: Math.abs(realWeights[j]) }))
      .sort((a, b) => b.absWeight - a.absWeight),
  };
};

// Polynomial regression (via linear regression on poly features)
export const polyRegression = (rows, xCol, yCol, degree = 2) => {
  const data = rows
    .map(r => [parseNum(r[xCol]), parseNum(r[yCol])])
    .filter(([x, y]) => !isNaN(x) && !isNaN(y) && isFinite(x) && isFinite(y));
  if (!data.length) return null;
  // Build poly feature matrix
  const polyRows = data.map(([x, y]) => {
    const obj = { y: String(y) };
    for (let d = 1; d <= degree; d++) obj[`x^${d}`] = String(Math.pow(x, d));
    return obj;
  });
  const xCols = Array.from({ length: degree }, (_, i) => `x^${i + 1}`);
  const result = multiLinearRegression(polyRows, xCols, 'y', { lr: 0.001, epochs: 2000 });
  if (!result) return null;
  // For curve plotting: x range
  const xs = data.map(([x]) => x);
  const mn = Math.min(...xs), mx = Math.max(...xs);
  const curve = Array.from({ length: 60 }, (_, i) => {
    const x = mn + (mx - mn) * i / 59;
    return { x: +x.toFixed(3), actual: null };
  });
  return { ...result, data, curve, degree };
};

// Ridge / Lasso comparison
export const ridgeLasso = (rows, xCols, yCol) => {
  const alphas = [0, 0.001, 0.01, 0.1, 1, 10];
  return alphas.map(alpha => {
    const r = multiLinearRegression(rows, xCols, yCol, { lr: 0.01, epochs: 500, regularize: alpha });
    return r ? { alpha, r2: r.r2, rmse: r.rmse } : null;
  }).filter(Boolean);
};

// ─────────────────────────────────────────────
// CLASSIFICATION
// ─────────────────────────────────────────────

// k-NN classifier
export const kNN = (trainRows, testRows, featureCols, labelCol, k = 3) => {
  const train = trainRows.map(r => ({
    features: featureCols.map(c => parseNum(r[c])),
    label: r[labelCol],
  })).filter(d => d.features.every(v => !isNaN(v)));

  const results = testRows.map(r => {
    const q = featureCols.map(c => parseNum(r[c]));
    if (q.some(v => isNaN(v))) return { predicted: null, actual: r[labelCol] };
    const dists = train.map(t => ({ label: t.label, dist: euclidean(q, t.features) }))
      .sort((a, b) => a.dist - b.dist).slice(0, k);
    const votes = {};
    dists.forEach(d => { votes[d.label] = (votes[d.label] || 0) + 1; });
    const predicted = Object.entries(votes).sort((a, b) => b[1] - a[1])[0][0];
    return { predicted, actual: r[labelCol], correct: predicted === r[labelCol], neighbors: dists };
  });

  return results;
};

// Logistic regression (binary, gradient descent)
export const logisticRegression = (rows, featureCols, labelCol, opts = {}) => {
  const { lr = 0.1, epochs = 500 } = opts;
  const classes = [...new Set(rows.map(r => r[labelCol]).filter(Boolean))].sort();
  if (classes.length !== 2) return null;

  const data = rows.map(r => ({
    x: featureCols.map(c => parseNum(r[c])),
    y: r[labelCol] === classes[1] ? 1 : 0,
  })).filter(d => d.x.every(v => !isNaN(v) && isFinite(v)));

  if (data.length < 10) return null;
  const n = data.length;

  const sig = z => 1 / (1 + Math.exp(-Math.max(-500, Math.min(500, z))));

  // Normalize
  const scalers = featureCols.map((_, ci) => {
    const v = data.map(d => d.x[ci]);
    return { m: mean(v), s: std(v) || 1 };
  });
  const Xn = data.map(d => d.x.map((v, ci) => (v - scalers[ci].m) / scalers[ci].s));

  let w = new Array(featureCols.length).fill(0);
  let b = 0;
  const losses = [];

  for (let e = 0; e < epochs; e++) {
    let wG = new Array(featureCols.length).fill(0);
    let bG = 0, loss = 0;
    Xn.forEach((x, i) => {
      const p = sig(x.reduce((s, v, j) => s + v * w[j], b));
      const err = p - data[i].y;
      loss -= data[i].y * Math.log(p + 1e-15) + (1 - data[i].y) * Math.log(1 - p + 1e-15);
      x.forEach((v, j) => { wG[j] += err * v; });
      bG += err;
    });
    if (e % 25 === 0) losses.push({ epoch: e, loss: +(loss / n).toFixed(4) });
    w = w.map((wj, j) => wj - lr * wG[j] / n);
    b -= lr * bG / n;
  }

  // Predictions
  const predictions = data.map((d, i) => {
    const prob = sig(Xn[i].reduce((s, v, j) => s + v * w[j], b));
    return { actual: d.y, prob: +prob.toFixed(4), predicted: prob >= 0.5 ? 1 : 0 };
  });

  return { predictions, lossHistory: losses, classes, featureCols, n };
};

// Naive Bayes (Gaussian)
export const naiveBayes = (trainRows, testRows, featureCols, labelCol) => {
  const classes = [...new Set(trainRows.map(r => r[labelCol]).filter(Boolean))];
  const stats = {};
  classes.forEach(cls => {
    const clsRows = trainRows.filter(r => r[labelCol] === cls);
    stats[cls] = {
      prior: clsRows.length / trainRows.length,
      features: featureCols.map(col => {
        const vals = clsRows.map(r => parseNum(r[col])).filter(v => !isNaN(v));
        const m = vals.length ? mean(vals) : 0;
        const s = vals.length > 1 ? std(vals) : 1;
        return { mean: m, std: s || 0.1 };
      }),
    };
  });

  const pdf = (x, m, s) => Math.exp(-0.5 * ((x - m) / s) ** 2) / (s * Math.sqrt(2 * Math.PI));

  const predictions = testRows.map(r => {
    const x = featureCols.map(c => parseNum(r[c]));
    const scores = {};
    classes.forEach(cls => {
      let logP = Math.log(stats[cls].prior);
      featureCols.forEach((_, fi) => {
        if (!isNaN(x[fi])) {
          const p = pdf(x[fi], stats[cls].features[fi].mean, stats[cls].features[fi].std);
          logP += Math.log(p + 1e-300);
        }
      });
      scores[cls] = logP;
    });
    const predicted = Object.entries(scores).sort((a, b) => b[1] - a[1])[0][0];
    return { predicted, actual: r[labelCol], correct: predicted === r[labelCol], scores };
  });

  return { predictions, classes, stats };
};

// Decision tree (CART, binary classification, max depth 5)
export const decisionTree = (rows, featureCols, labelCol, maxDepth = 5) => {
  const classes = [...new Set(rows.map(r => r[labelCol]).filter(Boolean))];
  const gini = (subset) => {
    const total = subset.length;
    if (!total) return 0;
    const freq = {};
    subset.forEach(r => { freq[r[labelCol]] = (freq[r[labelCol]] || 0) + 1; });
    return 1 - Object.values(freq).reduce((s, c) => s + (c / total) ** 2, 0);
  };
  const bestSplit = (subset) => {
    let best = { gain: -Infinity, col: null, thresh: null };
    const parentGini = gini(subset);
    featureCols.forEach(col => {
      const vals = [...new Set(subset.map(r => parseNum(r[col])).filter(v => !isNaN(v)))].sort((a, b) => a - b);
      vals.slice(0, -1).forEach((v, i) => {
        const thresh = (v + vals[i + 1]) / 2;
        const left = subset.filter(r => parseNum(r[col]) <= thresh);
        const right = subset.filter(r => parseNum(r[col]) > thresh);
        if (!left.length || !right.length) return;
        const gain = parentGini - (left.length / subset.length) * gini(left) - (right.length / subset.length) * gini(right);
        if (gain > best.gain) best = { gain, col, thresh };
      });
    });
    return best;
  };
  const majorityClass = (subset) => {
    const freq = {};
    subset.forEach(r => { freq[r[labelCol]] = (freq[r[labelCol]] || 0) + 1; });
    return Object.entries(freq).sort((a, b) => b[1] - a[1])[0]?.[0];
  };
  const buildTree = (subset, depth = 0) => {
    if (!subset.length || depth >= maxDepth) return { leaf: true, prediction: majorityClass(subset), n: subset.length };
    const labels = subset.map(r => r[labelCol]);
    if (new Set(labels).size === 1) return { leaf: true, prediction: labels[0], n: subset.length };
    const { col, thresh, gain } = bestSplit(subset);
    if (!col || gain <= 0) return { leaf: true, prediction: majorityClass(subset), n: subset.length };
    const left = subset.filter(r => parseNum(r[col]) <= thresh);
    const right = subset.filter(r => parseNum(r[col]) > thresh);
    return { col, thresh: +thresh.toFixed(4), gain: +gain.toFixed(4), n: subset.length, depth, left: buildTree(left, depth + 1), right: buildTree(right, depth + 1) };
  };

  const tree = buildTree(rows);
  const predict = (row, node) => {
    if (node.leaf) return node.prediction;
    const v = parseNum(row[node.col]);
    return isNaN(v) ? node.prediction : predict(row, v <= node.thresh ? node.left : node.right);
  };

  const predictions = rows.map(r => {
    const p = predict(r, tree);
    return { predicted: p, actual: r[labelCol], correct: p === r[labelCol] };
  });

  return { tree, predictions, classes };
};

// Confusion matrix + metrics from predictions
export const confusionMatrix = (predictions, classes) => {
  const mat = {};
  classes.forEach(a => { mat[a] = {}; classes.forEach(p => { mat[a][p] = 0; }); });
  predictions.forEach(({ actual, predicted }) => {
    if (mat[actual] && predicted) mat[actual][predicted] = (mat[actual][predicted] || 0) + 1;
  });
  const accuracy = predictions.filter(p => p.correct).length / predictions.length;
  // Per-class metrics
  const classMetrics = classes.map(cls => {
    const tp = mat[cls]?.[cls] || 0;
    const fp = classes.reduce((s, c) => s + (c !== cls ? (mat[c]?.[cls] || 0) : 0), 0);
    const fn = classes.reduce((s, c) => s + (c !== cls ? (mat[cls]?.[c] || 0) : 0), 0);
    const precision = tp + fp > 0 ? tp / (tp + fp) : 0;
    const recall = tp + fn > 0 ? tp / (tp + fn) : 0;
    const f1 = precision + recall > 0 ? 2 * precision * recall / (precision + recall) : 0;
    return { cls, tp, fp, fn, precision: +precision.toFixed(4), recall: +recall.toFixed(4), f1: +f1.toFixed(4) };
  });
  const macroF1 = mean(classMetrics.map(m => m.f1));
  return { mat, accuracy: +accuracy.toFixed(4), classMetrics, macroF1: +macroF1.toFixed(4), n: predictions.length };
};

// Train/test split
export const trainTestSplit = (rows, testRatio = 0.2, seed = 42) => {
  const shuffled = [...rows].sort(() => Math.sin(seed++) * 10000 - Math.floor(Math.sin(seed) * 10000));
  const splitIdx = Math.floor(shuffled.length * (1 - testRatio));
  return { train: shuffled.slice(0, splitIdx), test: shuffled.slice(splitIdx) };
};

// k-fold cross-validation scores
export const kFoldCV = (rows, k = 5, modelFn) => {
  const fold = Math.floor(rows.length / k);
  return Array.from({ length: k }, (_, i) => {
    const test = rows.slice(i * fold, (i + 1) * fold);
    const train = [...rows.slice(0, i * fold), ...rows.slice((i + 1) * fold)];
    return modelFn(train, test);
  }).filter(Boolean);
};

// ─────────────────────────────────────────────
// CLUSTERING
// ─────────────────────────────────────────────

// k-Means
export const kMeans = (rows, featureCols, k = 3, maxIter = 100) => {
  const data = rows.map(r => featureCols.map(c => parseNum(r[c]))).filter(r => r.every(v => !isNaN(v) && isFinite(v)));
  if (data.length < k) return null;
  const n = data.length;

  // k-means++ initialization
  let centers = [data[Math.floor(Math.random() * n)]];
  while (centers.length < k) {
    const dists = data.map(p => Math.min(...centers.map(c => euclidean(p, c) ** 2)));
    const total = dists.reduce((s, v) => s + v, 0);
    let r = Math.random() * total;
    for (let i = 0; i < n; i++) { r -= dists[i]; if (r <= 0) { centers.push(data[i]); break; } }
    if (centers.length < k) centers.push(data[n - 1]);
  }

  let labels = new Array(n).fill(0);
  const history = [];

  for (let iter = 0; iter < maxIter; iter++) {
    // Assign
    const newLabels = data.map(p => {
      const dists = centers.map(c => euclidean(p, c));
      return dists.indexOf(Math.min(...dists));
    });

    // Check convergence
    if (JSON.stringify(newLabels) === JSON.stringify(labels)) break;
    labels = newLabels;

    // Update centers
    centers = Array.from({ length: k }, (_, ki) => {
      const pts = data.filter((_, i) => labels[i] === ki);
      if (!pts.length) return centers[ki];
      return featureCols.map((_, ci) => mean(pts.map(p => p[ci])));
    });

    const inertia = data.reduce((s, p, i) => s + euclidean(p, centers[labels[i]]) ** 2, 0);
    history.push({ iter, inertia: +inertia.toFixed(2) });
  }

  const inertia = data.reduce((s, p, i) => s + euclidean(p, centers[labels[i]]) ** 2, 0);

  // Cluster stats
  const clusterStats = Array.from({ length: k }, (_, ki) => {
    const pts = data.filter((_, i) => labels[i] === ki);
    const center = centers[ki];
    const avgDist = pts.length ? mean(pts.map(p => euclidean(p, center))) : 0;
    return { cluster: ki, size: pts.length, center: center.map(v => +v.toFixed(3)), avgDist: +avgDist.toFixed(3) };
  });

  // Silhouette score (simplified)
  const sil = data.map((p, i) => {
    const own = data.filter((_, j) => labels[j] === labels[i] && j !== i);
    if (!own.length) return 0;
    const a = mean(own.map(q => euclidean(p, q)));
    const otherClusters = [...new Set(labels)].filter(c => c !== labels[i]);
    if (!otherClusters.length) return 0;
    const b = Math.min(...otherClusters.map(c => {
      const pts = data.filter((_, j) => labels[j] === c);
      return pts.length ? mean(pts.map(q => euclidean(p, q))) : Infinity;
    }));
    return (b - a) / Math.max(a, b);
  });
  const silScore = +mean(sil.filter(isFinite)).toFixed(4);

  return {
    labels, centers: centers.map(c => c.map(v => +v.toFixed(4))),
    inertia: +inertia.toFixed(2), silScore, clusterStats, history,
    points: data.map((p, i) => ({ x: p[0], y: p[1], cluster: labels[i] })),
    k, n,
  };
};

// Elbow method: run k-means for k=1..8
export const elbowMethod = (rows, featureCols) => {
  const ks = [1, 2, 3, 4, 5, 6, 7, 8];
  return ks.map(k => {
    const result = kMeans(rows, featureCols, k, 50);
    return { k, inertia: result?.inertia || 0, silScore: result?.silScore || 0 };
  });
};

// DBSCAN
export const dbscan = (rows, featureCols, eps = 0.5, minPts = 5) => {
  const data = rows.map(r => featureCols.map(c => parseNum(r[c]))).filter(r => r.every(v => !isNaN(v) && isFinite(v)));
  if (!data.length) return null;
  const n = data.length;
  const labels = new Array(n).fill(-2); // -2 = unvisited
  let clusterIdx = 0;

  const neighbors = (i) => data.map((_, j) => j).filter(j => j !== i && euclidean(data[i], data[j]) <= eps);

  for (let i = 0; i < n; i++) {
    if (labels[i] !== -2) continue;
    const nbs = neighbors(i);
    if (nbs.length < minPts) { labels[i] = -1; continue; } // noise
    labels[i] = clusterIdx;
    const seeds = [...nbs];
    while (seeds.length) {
      const j = seeds.shift();
      if (labels[j] === -1) labels[j] = clusterIdx;
      if (labels[j] !== -2) continue;
      labels[j] = clusterIdx;
      const nbs2 = neighbors(j);
      if (nbs2.length >= minPts) seeds.push(...nbs2.filter(nb => labels[nb] === -2 || labels[nb] === -1));
    }
    clusterIdx++;
  }

  const clusters = [...new Set(labels.filter(l => l >= 0))];
  const noiseCount = labels.filter(l => l === -1).length;

  return {
    labels, clusters, clusterCount: clusterIdx, noiseCount,
    noisePct: +(noiseCount / n * 100).toFixed(1),
    points: data.map((p, i) => ({ x: p[0], y: p[1], cluster: labels[i] })),
    clusterSizes: clusters.map(c => ({ cluster: c, size: labels.filter(l => l === c).length })),
    n,
  };
};

// Hierarchical clustering (agglomerative, single linkage)
export const hierarchical = (rows, featureCols, maxClusters = 5) => {
  const data = rows.slice(0, 100).map(r => featureCols.map(c => parseNum(r[c]))).filter(r => r.every(v => !isNaN(v) && isFinite(v)));
  if (data.length < 2) return null;
  const n = data.length;
  let clusters = data.map((_, i) => [i]);
  const dendrogram = [];

  while (clusters.length > 1) {
    let best = { dist: Infinity, a: 0, b: 1 };
    for (let i = 0; i < clusters.length; i++) {
      for (let j = i + 1; j < clusters.length; j++) {
        // single linkage: min distance between any two points
        let d = Infinity;
        clusters[i].forEach(pi => clusters[j].forEach(pj => {
          const dist = euclidean(data[pi], data[pj]);
          if (dist < d) d = dist;
        }));
        if (d < best.dist) best = { dist: d, a: i, b: j };
      }
    }
    const merged = [...clusters[best.a], ...clusters[best.b]];
    dendrogram.push({ step: n - clusters.length, dist: +best.dist.toFixed(4), size: merged.length });
    clusters = clusters.filter((_, i) => i !== best.a && i !== best.b);
    clusters.push(merged);
    if (clusters.length <= maxClusters) break;
  }

  const labels = new Array(n).fill(0);
  clusters.forEach((cluster, ci) => cluster.forEach(i => { labels[i] = ci; }));

  return {
    labels, clusterCount: clusters.length, dendrogram,
    points: data.map((p, i) => ({ x: p[0], y: p[1], cluster: labels[i] })),
    clusterSizes: clusters.map((c, i) => ({ cluster: i, size: c.length })),
  };
};

// ─────────────────────────────────────────────
// MODEL EVALUATION
// ─────────────────────────────────────────────

// R² score
export const r2Score = (actuals, predictions) => {
  const yMean = mean(actuals);
  const ssTot = actuals.reduce((s, v) => s + (v - yMean) ** 2, 0);
  const ssRes = actuals.reduce((s, v, i) => s + (v - predictions[i]) ** 2, 0);
  return ssTot ? +(1 - ssRes / ssTot).toFixed(4) : 0;
};

// Learning curve
export const learningCurve = (rows, xCols, yCol, sizes = [0.2, 0.4, 0.6, 0.8, 1.0]) => {
  return sizes.map(frac => {
    const n = Math.floor(rows.length * frac);
    const subset = rows.slice(0, n);
    if (subset.length < xCols.length + 2) return null;
    const { train, test } = trainTestSplit(subset, 0.2);
    const model = multiLinearRegression(train, xCols, yCol, { lr: 0.01, epochs: 200 });
    if (!model) return null;
    const trainPreds = model.predictions;
    const trainActuals = train.map(r => parseNum(r[yCol])).filter(v => !isNaN(v));
    const trainR2 = r2Score(trainActuals.slice(0, trainPreds.length), trainPreds);
    return { trainSize: n, trainR2, testR2: model.r2, rmse: model.rmse };
  }).filter(Boolean);
};

// Bias-Variance decomposition (simplified)
export const biasVariance = (rows, xCols, yCol, nTrials = 10) => {
  const complexities = [1, 2, 5, 10, 20];
  return complexities.map(maxEpochs => {
    const results = Array.from({ length: nTrials }, () => {
      const shuffled = [...rows].sort(() => Math.random() - 0.5);
      const { train, test } = trainTestSplit(shuffled, 0.3);
      const model = multiLinearRegression(train, xCols, yCol, { lr: 0.01, epochs: maxEpochs * 50 });
      return model;
    }).filter(Boolean);
    if (!results.length) return null;
    const testR2s = results.map(r => r.r2);
    const trainR2s = results.map(r => r.adjR2);
    return {
      complexity: maxEpochs,
      epochs: maxEpochs * 50,
      avgTrainR2: +mean(trainR2s).toFixed(4),
      avgTestR2: +mean(testR2s).toFixed(4),
      bias: +(1 - mean(testR2s)).toFixed(4),
      variance: +variance(testR2s).toFixed(6),
    };
  }).filter(Boolean);
};

// ROC curve data
export const rocCurve = (predictions) => {
  const thresholds = Array.from({ length: 21 }, (_, i) => i / 20);
  return thresholds.map(thresh => {
    const preds = predictions.map(p => ({ ...p, threshPred: (p.prob || 0) >= thresh ? 1 : 0 }));
    const tp = preds.filter(p => p.threshPred === 1 && p.actual === 1).length;
    const fp = preds.filter(p => p.threshPred === 1 && p.actual === 0).length;
    const fn = preds.filter(p => p.threshPred === 0 && p.actual === 1).length;
    const tn = preds.filter(p => p.threshPred === 0 && p.actual === 0).length;
    const tpr = tp + fn > 0 ? tp / (tp + fn) : 0;
    const fpr = fp + tn > 0 ? fp / (fp + tn) : 0;
    return { threshold: thresh, tpr: +tpr.toFixed(3), fpr: +fpr.toFixed(3) };
  });
};

// AUC (trapezoidal rule)
export const auc = (roc) => {
  let area = 0;
  const sorted = [...roc].sort((a, b) => a.fpr - b.fpr);
  for (let i = 1; i < sorted.length; i++) {
    area += (sorted[i].fpr - sorted[i - 1].fpr) * (sorted[i].tpr + sorted[i - 1].tpr) / 2;
  }
  return +Math.abs(area).toFixed(4);
};

// Feature importance via permutation
export const permutationImportance = (rows, featureCols, yCol, baselineR2) => {
  return featureCols.map(col => {
    const shuffled = rows.map((r, i) => ({ ...r, [col]: rows[(i + Math.floor(rows.length / 2)) % rows.length][col] }));
    const model = multiLinearRegression(shuffled, featureCols, yCol, { lr: 0.01, epochs: 200 });
    const drop = baselineR2 - (model?.r2 || 0);
    return { col, importance: +Math.max(0, drop).toFixed(4), r2Without: model?.r2 || 0 };
  }).sort((a, b) => b.importance - a.importance);
};

// Generate mock dataset
export const generateMockDataset = (type) => {
  const n = 200;
  if (type === 'linear') {
    const rows = Array.from({ length: n }, (_, i) => {
      const x1 = Math.random() * 10, x2 = Math.random() * 5;
      const noise = (Math.random() - 0.5) * 3;
      const y = 2.5 * x1 + 1.8 * x2 + noise + 5;
      return { x1: x1.toFixed(2), x2: x2.toFixed(2), y: y.toFixed(2) };
    });
    return { rows, headers: ['x1', 'x2', 'y'], shape: [n, 3] };
  }
  if (type === 'classification') {
    const rows = Array.from({ length: n }, () => {
      const cls = Math.random() > 0.5 ? 'A' : 'B';
      const x1 = (cls === 'A' ? 2 : 6) + (Math.random() - 0.5) * 3;
      const x2 = (cls === 'A' ? 3 : 5) + (Math.random() - 0.5) * 2;
      const x3 = Math.random() * 10;
      return { x1: x1.toFixed(2), x2: x2.toFixed(2), x3: x3.toFixed(2), class: cls };
    });
    return { rows, headers: ['x1', 'x2', 'x3', 'class'], shape: [n, 4] };
  }
  if (type === 'multiclass') {
    const classes = ['Alpha', 'Beta', 'Gamma'];
    const rows = Array.from({ length: n }, () => {
      const cls = classes[Math.floor(Math.random() * 3)];
      const ci = classes.indexOf(cls);
      const x1 = ci * 4 + (Math.random() - 0.5) * 3;
      const x2 = (2 - ci) * 3 + (Math.random() - 0.5) * 2;
      const x3 = Math.random() * 8;
      const x4 = Math.sin(ci) + (Math.random() - 0.5);
      return { x1: x1.toFixed(2), x2: x2.toFixed(2), x3: x3.toFixed(2), x4: x4.toFixed(2), class: cls };
    });
    return { rows, headers: ['x1', 'x2', 'x3', 'x4', 'class'], shape: [n, 5] };
  }
  if (type === 'clustering') {
    const centers = [[2, 2], [7, 2], [4, 8]];
    const rows = Array.from({ length: n }, () => {
      const ci = Math.floor(Math.random() * 3);
      const x1 = centers[ci][0] + (Math.random() - 0.5) * 3;
      const x2 = centers[ci][1] + (Math.random() - 0.5) * 3;
      const x3 = Math.random() * 5;
      return { x1: x1.toFixed(2), x2: x2.toFixed(2), x3: x3.toFixed(2) };
    });
    return { rows, headers: ['x1', 'x2', 'x3'], shape: [n, 3] };
  }
  if (type === 'housing') {
    const rows = Array.from({ length: n }, () => {
      const rooms = Math.floor(Math.random() * 6 + 1);
      const sqft = rooms * 300 + Math.random() * 500;
      const age = Math.floor(Math.random() * 50);
      const dist = Math.random() * 20;
      const price = (rooms * 50000 + sqft * 100 - age * 1000 - dist * 2000 + (Math.random() - 0.5) * 50000);
      return { rooms: String(rooms), sqft: sqft.toFixed(0), age: String(age), dist_km: dist.toFixed(1), price: Math.max(50000, price).toFixed(0) };
    });
    return { rows, headers: ['rooms', 'sqft', 'age', 'dist_km', 'price'], shape: [n, 5] };
  }
  return generateMockDataset('linear');
};

// ─────────────────────────────────────────────
// EXTENDED REGRESSION ALGORITHMS
// ─────────────────────────────────────────────

export const elasticNetRegression = (rows, xCols, yCol, opts = {}) => {
  const { l1Ratio = 0.5, alpha = 0.1, lr = 0.01, epochs = 500 } = opts;
  if (!rows.length || !xCols.length) return null;
  const X = rows.map(r => xCols.map(c => parseNum(r[c]))).filter(x => x.every(v => !isNaN(v)));
  const Y = rows.map(r => parseNum(r[yCol])).filter(v => !isNaN(v));
  const n = Math.min(X.length, Y.length);
  if (n < 4) return null;
  // Z-score normalize
  const means = xCols.map((_, j) => mean(X.map(x => x[j])));
  const stds = xCols.map((_, j) => std(X.map(x => x[j])));
  const Xn = X.slice(0, n).map(x => x.map((v, j) => stds[j] > 0 ? (v - means[j]) / stds[j] : 0));
  const Yn = Y.slice(0, n);
  let w = new Array(xCols.length).fill(0), b = 0;
  for (let e = 0; e < epochs; e++) {
    let dw = new Array(xCols.length).fill(0), db = 0;
    for (let i = 0; i < n; i++) {
      const pred = Xn[i].reduce((s, x, j) => s + x * w[j], b);
      const err = pred - Yn[i];
      Xn[i].forEach((x, j) => { dw[j] += err * x; });
      db += err;
    }
    w = w.map((wi, j) => {
      const grad = dw[j] / n + alpha * (l1Ratio * Math.sign(wi) + (1 - l1Ratio) * 2 * wi);
      return wi - lr * grad;
    });
    b -= lr * db / n;
  }
  const wOrig = w.map((wi, j) => stds[j] > 0 ? wi / stds[j] : wi);
  const bOrig = b - wOrig.reduce((s, wi, j) => s + wi * means[j], 0);
  const preds = rows.slice(0, n).map((_, i) => Xn[i].reduce((s, x, j) => s + x * w[j], b));
  const actuals = Yn;
  const ssRes = actuals.reduce((s, y, i) => s + (y - preds[i]) ** 2, 0);
  const ssTot = actuals.reduce((s, y) => s + (y - mean(actuals)) ** 2, 0);
  const r2 = ssTot > 0 ? Math.max(0, +(1 - ssRes / ssTot).toFixed(4)) : 0;
  const rmse = +(Math.sqrt(ssRes / n)).toFixed(4);
  const mae = +(actuals.reduce((s, y, i) => s + Math.abs(y - preds[i]), 0) / n).toFixed(4);
  return { r2, rmse, mae, n, weights: wOrig, bias: bOrig, predictions: preds, xCols };
};

export const gradientBoostingRegression = (rows, xCols, yCol, opts = {}) => {
  const { nEstimators = 50, lr = 0.1, maxDepth = 3 } = opts;
  if (!rows.length || !xCols.length) return null;
  const X = rows.map(r => xCols.map(c => parseNum(r[c])));
  const Y = rows.map(r => parseNum(r[yCol]));
  const valid = X.map((x, i) => x.every(v => !isNaN(v)) && !isNaN(Y[i])).map((v, i) => v ? i : -1).filter(i => i >= 0);
  if (valid.length < 4) return null;
  const Xv = valid.map(i => X[i]), Yv = valid.map(i => Y[i]);
  const n = Xv.length;
  // Simple regression tree stump
  const fitStump = (residuals) => {
    let bestCol = 0, bestThresh = 0, bestGain = Infinity;
    xCols.forEach((_, j) => {
      const vals = Xv.map(x => x[j]).sort((a, b) => a - b);
      for (let t = 0; t < vals.length - 1; t++) {
        const thresh = (vals[t] + vals[t + 1]) / 2;
        const left = residuals.filter((_, i) => Xv[i][j] <= thresh);
        const right = residuals.filter((_, i) => Xv[i][j] > thresh);
        if (!left.length || !right.length) continue;
        const mse = (left.reduce((s, v) => s + (v - mean(left)) ** 2, 0) + right.reduce((s, v) => s + (v - mean(right)) ** 2, 0)) / n;
        if (mse < bestGain) { bestGain = mse; bestCol = j; bestThresh = thresh; }
      }
    });
    const lMean = mean(residuals.filter((_, i) => Xv[i][bestCol] <= bestThresh));
    const rMean = mean(residuals.filter((_, i) => Xv[i][bestCol] > bestThresh));
    return { col: bestCol, thresh: bestThresh, lMean: lMean || 0, rMean: rMean || 0 };
  };
  const predict1 = (stump, x) => x[stump.col] <= stump.thresh ? stump.lMean : stump.rMean;
  let F = new Array(n).fill(mean(Yv));
  const stumps = [];
  for (let t = 0; t < nEstimators; t++) {
    const residuals = Yv.map((y, i) => y - F[i]);
    const stump = fitStump(residuals);
    stumps.push(stump);
    F = F.map((f, i) => f + lr * predict1(stump, Xv[i]));
  }
  const preds = Xv.map((_, i) => F[i]);
  const ssRes = Yv.reduce((s, y, i) => s + (y - preds[i]) ** 2, 0);
  const ssTot = Yv.reduce((s, y) => s + (y - mean(Yv)) ** 2, 0);
  const r2 = ssTot > 0 ? Math.max(0, +(1 - ssRes / ssTot).toFixed(4)) : 0;
  const rmse = +(Math.sqrt(ssRes / n)).toFixed(4);
  const mae = +(Yv.reduce((s, y, i) => s + Math.abs(y - preds[i]), 0) / n).toFixed(4);
  return { r2, rmse, mae, n, predictions: preds, nEstimators };
};

export const randomForestRegression = (rows, xCols, yCol, opts = {}) => {
  const { nTrees = 20, maxDepth = 4 } = opts;
  if (!rows.length || !xCols.length) return null;
  const X = rows.map(r => xCols.map(c => parseNum(r[c])));
  const Y = rows.map(r => parseNum(r[yCol]));
  const valid = X.map((x, i) => x.every(v => !isNaN(v)) && !isNaN(Y[i])).map((v, i) => v ? i : -1).filter(i => i >= 0);
  if (valid.length < 4) return null;
  const Xv = valid.map(i => X[i]), Yv = valid.map(i => Y[i]);
  const n = Xv.length;
  const buildTree = (idxs, depth) => {
    if (!idxs.length || depth >= maxDepth) return { leaf: true, val: mean(idxs.map(i => Yv[i])) };
    const ys = idxs.map(i => Yv[i]);
    const baseMse = variance(ys);
    const nFeats = Math.max(1, Math.floor(Math.sqrt(xCols.length)));
    const featIdxs = [...Array(xCols.length).keys()].sort(() => Math.random() - 0.5).slice(0, nFeats);
    let bestGain = 0, bestCol = featIdxs[0], bestThresh = 0;
    for (const j of featIdxs) {
      const vals = [...new Set(idxs.map(i => Xv[i][j]))].sort((a, b) => a - b);
      for (let t = 0; t < vals.length - 1; t++) {
        const thresh = (vals[t] + vals[t + 1]) / 2;
        const L = idxs.filter(i => Xv[i][j] <= thresh);
        const R = idxs.filter(i => Xv[i][j] > thresh);
        if (!L.length || !R.length) continue;
        const gain = baseMse - (L.length / idxs.length) * variance(L.map(i => Yv[i])) - (R.length / idxs.length) * variance(R.map(i => Yv[i]));
        if (gain > bestGain) { bestGain = gain; bestCol = j; bestThresh = thresh; }
      }
    }
    if (bestGain <= 0) return { leaf: true, val: mean(ys) };
    const L = idxs.filter(i => Xv[i][bestCol] <= bestThresh);
    const R = idxs.filter(i => Xv[i][bestCol] > bestThresh);
    return { col: bestCol, thresh: bestThresh, left: buildTree(L, depth + 1), right: buildTree(R, depth + 1) };
  };
  const predictTree = (node, x) => node.leaf ? node.val : (x[node.col] <= node.thresh ? predictTree(node.left, x) : predictTree(node.right, x));
  // Bootstrap trees
  const trees = Array.from({ length: nTrees }, () => {
    const bootstrap = Array.from({ length: n }, () => Math.floor(Math.random() * n));
    return buildTree(bootstrap, 0);
  });
  const preds = Xv.map(x => mean(trees.map(t => predictTree(t, x))));
  const ssRes = Yv.reduce((s, y, i) => s + (y - preds[i]) ** 2, 0);
  const ssTot = Yv.reduce((s, y) => s + (y - mean(Yv)) ** 2, 0);
  const r2 = ssTot > 0 ? Math.max(0, +(1 - ssRes / ssTot).toFixed(4)) : 0;
  const rmse = +(Math.sqrt(ssRes / n)).toFixed(4);
  const mae = +(Yv.reduce((s, y, i) => s + Math.abs(y - preds[i]), 0) / n).toFixed(4);
  return { r2, rmse, mae, n, predictions: preds, nTrees };
};

// ─────────────────────────────────────────────
// EXTENDED CLASSIFICATION ALGORITHMS
// ─────────────────────────────────────────────

export const randomForestClassifier = (trainRows, testRows, featureCols, labelCol, opts = {}) => {
  const { nTrees = 20, maxDepth = 5 } = opts;
  const classes = [...new Set(trainRows.map(r => r[labelCol]).filter(Boolean))].sort();
  const encode = cls => classes.indexOf(cls);
  const X = trainRows.map(r => featureCols.map(c => parseNum(r[c])));
  const Y = trainRows.map(r => encode(r[labelCol]));
  const valid = X.map((x, i) => x.every(v => !isNaN(v)) && Y[i] >= 0).map((v, i) => v ? i : -1).filter(i => i >= 0);
  if (valid.length < 4) return null;
  const Xv = valid.map(i => X[i]), Yv = valid.map(i => Y[i]);
  const n = Xv.length;
  const gini = labels => { const tot = labels.length; if (!tot) return 0; const cnts = classes.map(c => encode(c)).map(ci => labels.filter(l => l === ci).length); return 1 - cnts.reduce((s, c) => s + (c / tot) ** 2, 0); };
  const buildTree = (idxs, depth) => {
    if (!idxs.length || depth >= maxDepth) { const ys = idxs.map(i => Yv[i]); const cnt = classes.map((_, ci) => ys.filter(y => y === ci).length); return { leaf: true, val: cnt.indexOf(Math.max(...cnt)) }; }
    const ys = idxs.map(i => Yv[i]);
    const nFeats = Math.max(1, Math.floor(Math.sqrt(featureCols.length)));
    const featIdxs = [...Array(featureCols.length).keys()].sort(() => Math.random() - 0.5).slice(0, nFeats);
    let bestGain = -1, bestCol = featIdxs[0], bestThresh = 0;
    for (const j of featIdxs) {
      const vals = [...new Set(idxs.map(i => Xv[i][j]))].sort((a, b) => a - b);
      for (let t = 0; t < vals.length - 1; t++) {
        const thresh = (vals[t] + vals[t + 1]) / 2;
        const L = idxs.filter(i => Xv[i][j] <= thresh);
        const R = idxs.filter(i => Xv[i][j] > thresh);
        if (!L.length || !R.length) continue;
        const gain = gini(ys) - (L.length / idxs.length) * gini(L.map(i => Yv[i])) - (R.length / idxs.length) * gini(R.map(i => Yv[i]));
        if (gain > bestGain) { bestGain = gain; bestCol = j; bestThresh = thresh; }
      }
    }
    if (bestGain <= 0) { const cnt = classes.map((_, ci) => ys.filter(y => y === ci).length); return { leaf: true, val: cnt.indexOf(Math.max(...cnt)) }; }
    const L = idxs.filter(i => Xv[i][bestCol] <= bestThresh);
    const R = idxs.filter(i => Xv[i][bestCol] > bestThresh);
    return { col: bestCol, thresh: bestThresh, left: buildTree(L, depth + 1), right: buildTree(R, depth + 1) };
  };
  const predictTree = (node, x) => node.leaf ? node.val : (x[node.col] <= node.thresh ? predictTree(node.left, x) : predictTree(node.right, x));
  const trees = Array.from({ length: nTrees }, () => {
    const bootstrap = Array.from({ length: n }, () => Math.floor(Math.random() * n));
    return buildTree(bootstrap, 0);
  });
  const Xt = testRows.map(r => featureCols.map(c => parseNum(r[c])));
  const Yt = testRows.map(r => r[labelCol]);
  const predictions = Xt.map((x, i) => {
    if (x.some(isNaN)) return { actual: Yt[i], predicted: null, correct: false };
    const votes = trees.map(t => predictTree(t, x));
    const cnt = classes.map((_, ci) => votes.filter(v => v === ci).length);
    return { actual: Yt[i], predicted: classes[cnt.indexOf(Math.max(...cnt))], correct: classes[cnt.indexOf(Math.max(...cnt))] === Yt[i] };
  });
  return { predictions: predictions.filter(p => p.predicted !== null), nTrees };
};

export const svmClassifier = (trainRows, testRows, featureCols, labelCol, opts = {}) => {
  // Linear SVM via SGD (Pegasos algorithm) — binary only
  const classes = [...new Set(trainRows.map(r => r[labelCol]).filter(Boolean))].sort();
  if (classes.length !== 2) return null;
  const encode = cls => cls === classes[1] ? 1 : -1;
  const X = trainRows.map(r => featureCols.map(c => parseNum(r[c])));
  const Y = trainRows.map(r => encode(r[labelCol]));
  const valid = X.map((x, i) => x.every(v => !isNaN(v)) && !isNaN(Y[i])).map((v, i) => v ? i : -1).filter(i => i >= 0);
  if (valid.length < 4) return null;
  const Xv = valid.map(i => X[i]), Yv = valid.map(i => Y[i]);
  const n = Xv.length;
  const means = featureCols.map((_, j) => mean(Xv.map(x => x[j])));
  const stds = featureCols.map((_, j) => std(Xv.map(x => x[j])));
  const Xn = Xv.map(x => x.map((v, j) => stds[j] > 0 ? (v - means[j]) / stds[j] : 0));
  let w = new Array(featureCols.length).fill(0);
  const C = 1, epochs = 200;
  for (let e = 0; e < epochs; e++) {
    const lr = 1 / (C * (e + 1));
    for (let i = 0; i < n; i++) {
      const margin = Yv[i] * Xn[i].reduce((s, x, j) => s + x * w[j], 0);
      if (margin < 1) {
        w = w.map((wj, j) => (1 - lr * C) * wj + lr * Yv[i] * Xn[i][j]);
      } else {
        w = w.map(wj => (1 - lr * C) * wj);
      }
    }
  }
  const Xt = testRows.map(r => featureCols.map((c, j) => stds[j] > 0 ? (parseNum(r[c]) - means[j]) / stds[j] : 0));
  const Yt = testRows.map(r => r[labelCol]);
  const predictions = Xt.map((x, i) => {
    if (x.some(isNaN)) return { actual: Yt[i], predicted: null };
    const score = x.reduce((s, xi, j) => s + xi * w[j], 0);
    const predicted = score >= 0 ? classes[1] : classes[0];
    return { actual: Yt[i], predicted, correct: predicted === Yt[i] };
  });
  return { predictions: predictions.filter(p => p.predicted !== null) };
};

export const baggingClassifier = (trainRows, testRows, featureCols, labelCol, opts = {}) => {
  const { nEstimators = 15 } = opts;
  const classes = [...new Set(trainRows.map(r => r[labelCol]).filter(Boolean))].sort();
  const encode = cls => classes.indexOf(cls);
  const X = trainRows.map(r => featureCols.map(c => parseNum(r[c])));
  const Y = trainRows.map(r => encode(r[labelCol]));
  const valid = X.map((x, i) => x.every(v => !isNaN(v)) && Y[i] >= 0).map((v, i) => v ? i : -1).filter(i => i >= 0);
  if (valid.length < 4) return null;
  const Xv = valid.map(i => X[i]), Yv = valid.map(i => Y[i]);
  const n = Xv.length;
  const gini = labels => { const tot = labels.length; if (!tot) return 0; const cnts = classes.map((_, ci) => labels.filter(l => l === ci).length); return 1 - cnts.reduce((s, c) => s + (c / tot) ** 2, 0); };
  const buildStump = (idxs) => {
    let best = { gain: -1, col: 0, thresh: 0, lVal: 0, rVal: 0 };
    featureCols.forEach((_, j) => {
      const vals = [...new Set(idxs.map(i => Xv[i][j]))].sort((a, b) => a - b);
      for (let t = 0; t < Math.min(vals.length - 1, 20); t++) {
        const thresh = (vals[t] + vals[t + 1]) / 2;
        const L = idxs.filter(i => Xv[i][j] <= thresh);
        const R = idxs.filter(i => Xv[i][j] > thresh);
        if (!L.length || !R.length) continue;
        const ys = idxs.map(i => Yv[i]);
        const gain = gini(ys) - (L.length / idxs.length) * gini(L.map(i => Yv[i])) - (R.length / idxs.length) * gini(R.map(i => Yv[i]));
        if (gain > best.gain) {
          const lCnt = classes.map((_, ci) => L.map(i => Yv[i]).filter(y => y === ci).length);
          const rCnt = classes.map((_, ci) => R.map(i => Yv[i]).filter(y => y === ci).length);
          best = { gain, col: j, thresh, lVal: lCnt.indexOf(Math.max(...lCnt)), rVal: rCnt.indexOf(Math.max(...rCnt)) };
        }
      }
    });
    return best;
  };
  const stumps = Array.from({ length: nEstimators }, () => {
    const bootstrap = Array.from({ length: n }, () => Math.floor(Math.random() * n));
    return buildStump(bootstrap);
  });
  const predict = x => {
    const votes = stumps.map(s => x[s.col] <= s.thresh ? s.lVal : s.rVal);
    const cnt = classes.map((_, ci) => votes.filter(v => v === ci).length);
    return classes[cnt.indexOf(Math.max(...cnt))];
  };
  const Xt = testRows.map(r => featureCols.map(c => parseNum(r[c])));
  const Yt = testRows.map(r => r[labelCol]);
  const predictions = Xt.map((x, i) => {
    if (x.some(isNaN)) return { actual: Yt[i], predicted: null };
    const predicted = predict(x);
    return { actual: Yt[i], predicted, correct: predicted === Yt[i] };
  });
  return { predictions: predictions.filter(p => p.predicted !== null) };
};

export const adaBoostClassifier = (trainRows, testRows, featureCols, labelCol, opts = {}) => {
  const { nEstimators = 30 } = opts;
  const classes = [...new Set(trainRows.map(r => r[labelCol]).filter(Boolean))].sort();
  if (classes.length !== 2) return null;
  const encode = cls => cls === classes[1] ? 1 : -1;
  const X = trainRows.map(r => featureCols.map(c => parseNum(r[c])));
  const Y = trainRows.map(r => encode(r[labelCol]));
  const valid = X.map((x, i) => x.every(v => !isNaN(v)) && !isNaN(Y[i])).map((v, i) => v ? i : -1).filter(i => i >= 0);
  if (valid.length < 4) return null;
  const Xv = valid.map(i => X[i]), Yv = valid.map(i => Y[i]);
  const n = Xv.length;
  let weights = new Array(n).fill(1 / n);
  const ensemble = [];
  for (let t = 0; t < nEstimators; t++) {
    // Fit weighted stump
    let best = { err: 1, col: 0, thresh: 0, dir: 1 };
    featureCols.forEach((_, j) => {
      const vals = [...new Set(Xv.map(x => x[j]))].sort((a, b) => a - b);
      for (let k = 0; k < Math.min(vals.length - 1, 15); k++) {
        const thresh = (vals[k] + vals[k + 1]) / 2;
        for (const dir of [1, -1]) {
          const preds = Xv.map(x => dir * (x[j] <= thresh ? 1 : -1));
          const err = preds.reduce((s, p, i) => s + (p !== Yv[i] ? weights[i] : 0), 0);
          if (err < best.err) best = { err, col: j, thresh, dir };
        }
      }
    });
    const err = Math.max(1e-10, Math.min(1 - 1e-10, best.err));
    const alpha = 0.5 * Math.log((1 - err) / err);
    const preds = Xv.map(x => best.dir * (x[best.col] <= best.thresh ? 1 : -1));
    const Z = weights.reduce((s, w, i) => s + w * Math.exp(-alpha * Yv[i] * preds[i]), 0);
    weights = weights.map((w, i) => w * Math.exp(-alpha * Yv[i] * preds[i]) / Z);
    ensemble.push({ ...best, alpha });
  }
  const predictOne = x => {
    const score = ensemble.reduce((s, m) => s + m.alpha * m.dir * (x[m.col] <= m.thresh ? 1 : -1), 0);
    return score >= 0 ? classes[1] : classes[0];
  };
  const Xt = testRows.map(r => featureCols.map(c => parseNum(r[c])));
  const Yt = testRows.map(r => r[labelCol]);
  const predictions = Xt.map((x, i) => {
    if (x.some(isNaN)) return { actual: Yt[i], predicted: null };
    const predicted = predictOne(x);
    return { actual: Yt[i], predicted, correct: predicted === Yt[i] };
  });
  return { predictions: predictions.filter(p => p.predicted !== null) };
};